import { Checkbox, FormControlLabel } from '@mui/material';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { allPointOfInterestTemplates } from '@/components/Analysis/state';
import { PointOfInterest } from '@/__generated__/graphql';
import { PIT, BLISTER, PIT_TYPE } from '@/constants';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { convertTemplateNameToPoiType } from '@/utils';
import { PointOfInterestTemplateDocument } from '@/types';
import { usePoiDefectData } from '../../hooks/usePoiDefectData';

type MarkAsPitCheckboxProps = {
  onFieldChange?: (value: boolean) => void;
  disabled?: boolean;
};

export const MarkAsPitCheckbox = ({ onFieldChange, disabled }: MarkAsPitCheckboxProps) => {
  const allPoiTemplates = useAtomValue(allPointOfInterestTemplates);
  const [editPointOfInterest, setEditPointOfInterest] = useAtom(poiState.editPointOfInterest);
  const { switchTemplate } = usePoiDefectData();

  const template = editPointOfInterest?.template;
  const poiType = convertTemplateNameToPoiType(template?.key ?? template?.name);
  const isPitCheckBoxEnabled = poiType === PIT_TYPE;

  const pitTemplateExists = useMemo(() => {
    return allPoiTemplates.some((poiTemplate) =>
      poiTemplate.name.toUpperCase().includes(PIT.toUpperCase())
    );
  }, [allPoiTemplates]);

  const handleTemplateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;

      const newTemplate = allPoiTemplates.find((item) =>
        item.name.includes(newValue ? PIT : BLISTER)
      ) as PointOfInterestTemplateDocument | undefined;

      if (newTemplate) {
        const switchedData = switchTemplate(newTemplate) as Record<string, string | number>;
        const replacedTemplateFieldData = (newTemplate.fields ?? []).reduce<
          Record<string, string | number>
        >((acc, field) => {
          if (field?.key) {
            acc[field.key] = switchedData?.[field.key] ?? '';
          }
          return acc;
        }, {});

        setEditPointOfInterest((current) => {
          const currentSectionKey = current.editingSection?.key;
          const currentSectionLabel = current.editingSection?.label;
          const nextTemplateSections = (newTemplate.panelConfig?.tabs ?? []).flatMap(
            (tab) => tab.sections ?? []
          );
          const hasSectionContext = Boolean(current.editingSection);
          const newEditingSection =
            hasSectionContext && currentSectionKey
              ? nextTemplateSections.find((section) => section.key === currentSectionKey)
              : undefined;
          const newEditingSectionByLabel =
            hasSectionContext && !newEditingSection && currentSectionLabel
              ? nextTemplateSections.find((section) => section.label === currentSectionLabel)
              : undefined;
          const fallbackSection = hasSectionContext ? nextTemplateSections.at(0) : undefined;
          const resolvedEditingSection = hasSectionContext
            ? (newEditingSection ?? newEditingSectionByLabel ?? fallbackSection)
            : current.editingSection;
          const shouldSwitchToTemplateFlow = hasSectionContext && !resolvedEditingSection;
          return {
            ...current,
            template: newTemplate,
            pointOfInterest: {
              ...current.pointOfInterest,
              templateFieldData: replacedTemplateFieldData,
            } as PointOfInterest,
            formState: shouldSwitchToTemplateFlow ? 'Update-Template' : current.formState,
            editingSection: shouldSwitchToTemplateFlow ? undefined : resolvedEditingSection,
          };
        });
      }

      if (onFieldChange) onFieldChange(newValue);
    },
    [allPoiTemplates, onFieldChange, setEditPointOfInterest, switchTemplate]
  );

  if (!pitTemplateExists) return null;

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={isPitCheckBoxEnabled}
          onChange={handleTemplateChange}
          disabled={disabled}
        />
      }
      label="Mark this point of interest as Pit"
    />
  );
};
